// resources/js/Components/LocalTinyMCE.jsx
import React, { useEffect, useRef } from 'react';

export default function LocalTinyMCE({ value, onChange, onBlur, placeholder = '', height = 300 }) {
  const textareaRef = useRef(null);
  const editorRef = useRef(null);
  const isInitialized = useRef(false);
  const uniqueId = useRef(`tinymce-${Math.random().toString(36).substr(2, 9)}`);

  useEffect(() => {
    // Only initialize once per instance
    if (isInitialized.current) return;

    const initTinyMCE = () => {
      if (!window.tinymce) {
        console.error('TinyMCE not loaded');
        return;
      }

      // Make sure the textarea has the unique ID
      if (textareaRef.current) {
        textareaRef.current.id = uniqueId.current;
      }

      window.tinymce.init({
        selector: `#${uniqueId.current}`,
        height: height,
        menubar: false,
        branding: false,
        license_key: 'gpl', // ✅ Fix license warning
        
        // Local configuration - no cloud dependencies
        base_url: '/tinymce',
        suffix: '.min',
        
        plugins: [
          'advlist', 'autolink', 'lists', 'link', 'charmap', 'preview',
          'anchor', 'searchreplace', 'visualblocks', 'code', 'fullscreen',
          'insertdatetime', 'table', 'help', 'wordcount'
        ],
        
        toolbar: 'undo redo | blocks | ' +
          'bold italic forecolor | alignleft aligncenter ' +
          'alignright alignjustify | bullist numlist outdent indent | ' +
          'removeformat | link | code | help',
          
        content_style: `
          body { 
            font-family: -apple-system,BlinkMacSystemFont,San Francisco,Segoe UI,Roboto,Helvetica Neue,sans-serif; 
            font-size:14px;
            line-height: 1.6;
            margin: 1rem;
          }
        `,
        
        placeholder: placeholder,
        toolbar_mode: 'sliding',
        skin: 'oxide',
        content_css: 'default',
        // ✅ Remove forced_root_block (deprecated) or set to valid string
        // forced_root_block: false, // ❌ This is deprecated
        forced_root_block: 'p', // ✅ Use this instead, or remove entirely
        extended_valid_elements: '*[*]',
        convert_urls: false,
        
        setup: function(editor) {
          editorRef.current = editor;
          
          // Handle content changes
          editor.on('change keyup setcontent', () => {
            const content = editor.getContent();
            if (onChange) {
              onChange(content);
            }
          });


    editor.on('blur', () => {
      const content = editor.getContent();
      if (onBlur) {
        onBlur(content);
      }
    });
  },

        
        init_instance_callback: function(editor) {
          // Set initial content
          if (value) {
            editor.setContent(value);
          }
          isInitialized.current = true;
        }
      });
    };

    // Ensure TinyMCE is loaded before initializing
    const waitForTinyMCE = () => {
      if (window.tinymce) {
        initTinyMCE();
      } else {
        setTimeout(waitForTinyMCE, 100);
      }
    };

    // Load TinyMCE script if not already loaded
    if (!window.tinymce) {
      if (!document.querySelector('script[src="/tinymce/tinymce.min.js"]')) {
        const script = document.createElement('script');
        script.src = '/tinymce/tinymce.min.js';
        script.onload = waitForTinyMCE;
        script.onerror = () => {
          console.error('Failed to load TinyMCE from /tinymce/tinymce.min.js');
          console.log('Make sure you have copied TinyMCE files to public/tinymce/');
        };
        document.head.appendChild(script);
      } else {
        waitForTinyMCE();
      }
    } else {
      // Small delay to ensure DOM is ready
      setTimeout(initTinyMCE, 50);
    }

    // Cleanup
    return () => {
      if (editorRef.current && window.tinymce) {
        try {
          // Remove this specific editor instance
          const editor = window.tinymce.get(uniqueId.current);
          if (editor) {
            editor.destroy();
          }
        } catch (e) {
          console.warn('Error destroying TinyMCE editor:', e);
        }
      }
    };
  }, []); // No dependencies to prevent re-initialization

  // Update content when value prop changes
  useEffect(() => {
    if (editorRef.current && isInitialized.current) {
      const currentContent = editorRef.current.getContent();
      if (value !== currentContent) {
        editorRef.current.setContent(value || '');
      }
    }
  }, [value]);

  return (
    <div>
      <textarea 
        ref={textareaRef}
        id={uniqueId.current}
        defaultValue={value}
        style={{ width: '100%', minHeight: `${height}px` }}
      />
    </div>
  );
}